import { useEffect, useMemo } from 'react';
import { useElements } from '@stripe/react-stripe-js';
import { useSearch } from '@tanstack/react-location';
import { convertToCents } from '@frontend/currency';
import { useTranslation } from '@frontend/i18n';
import { Stepper, UserIcon } from '@frontend/design-system';
import { PatientDetailsForm, usePatientDetailsForm } from '../../stripe-components';
import { AffirmRedirectParams } from '../../types';
import { useStripeStepper } from '../stripe-stepper-context';

interface StripePatientDetailsStepProps {
  amount: number;
}

export const StripePatientDetailsStep = ({ amount }: StripePatientDetailsStepProps) => {
  const { t } = useTranslation('weave-pay');

  const elements = useElements();
  const params: AffirmRedirectParams = useSearch();

  const { paymentRequest, setPaymentRequest, portalStepperSettings } = useStripeStepper();

  const patientDetailsForm = usePatientDetailsForm({ requestedAmount: amount });
  const { isComplete, getErrors, getFieldProps, seedValues } = patientDetailsForm;

  useEffect(() => {
    if (params?.amount) seedValues({ amount: params.amount.toString() });
    if (params?.firstName) seedValues({ firstName: params.firstName });
    if (params?.lastName) seedValues({ lastName: params.lastName });
  }, [params]);

  const paymentAmount = useMemo(
    () => convertToCents(Number(getFieldProps('amount').value)),
    [getFieldProps('amount').value]
  );

  const isAmountValid = !!paymentAmount && !getErrors().amount;

  const isDetailsStepValid = isComplete && !!paymentRequest?.person?.email && isAmountValid;

  const handleDetailsStep = async () => {
    elements?.update({ amount: paymentAmount });
    setPaymentRequest({
      ...paymentRequest,
      amount: paymentAmount,
      person: {
        ...paymentRequest?.person,
        firstName: getFieldProps('firstName').value,
        lastName: getFieldProps('lastName').value,
      },
    });
  };

  const handleEmailChange = (email: string) => {
    setPaymentRequest({
      ...paymentRequest,
      person: {
        ...paymentRequest?.person,
        email: email,
      },
    });
  };

  return (
    <>
      <Stepper.Title icon={<UserIcon />}>{t('Payment Details')}</Stepper.Title>
      <Stepper.Content>
        <PatientDetailsForm
          requestedAmount={amount}
          onEmailChange={handleEmailChange}
          formProps={patientDetailsForm}
          amountEditable={portalStepperSettings.amountEditable}
        />
      </Stepper.Content>
      <Stepper.ButtonBar>
        <Stepper.NextButton isValid={isDetailsStepValid} onClick={handleDetailsStep} />
      </Stepper.ButtonBar>
    </>
  );
};
